/**
 * Lens math: convert focal length + sensor size to HFOV (and back), and
 * derive VFOV from HFOV using the camera's actual sensor aspect ratio.
 *
 * Used by the camera panel so editing focal length or sensor size on a
 * custom-spec camera auto-updates the FOV cone, instead of forcing the
 * user to remember the formula.
 *
 * Formulas:
 *   HFOV = 2 × atan(sensor_width / (2 × focal_length))   [focal ↔ angle]
 *   VFOV: equisolid, sin(VFOV/4) = sin(HFOV/4) × sensor_height / sensor_width
 *
 * The VFOV line used to read 2 × atan(tan(HFOV/2) × h/w), the rectilinear form.
 * See getVfovHalfRad for the datasheet evidence that replaced it.
 */

import { parseResolutionPair, SENSOR_SIZES } from './doriUtils';
import { groundReachM, frameDepressionRangeDeg } from './geometry/frustum';

/** HFOV clamp bounds (degrees). The floor sits at 0.05° so the long end of a
 *  long-range zoom is representable: 1000 mm on a 1/1.8" sensor is 0.41°, and
 *  a person at 30 km on a 4K sensor needs about 0.3°. The geometry + DORI math
 *  is finite and correct at these angles (getVfovHalfRad, doriUtils); a higher
 *  floor silently widens the rendered cone and shortens every DORI distance. */
export const MIN_HFOV_DEG = 0.05;
export const MAX_HFOV_DEG = 360;

/** Format an HFOV for display: two decimals below 1° (so 0.29° from a long
 *  zoom reads correctly), one below 10°, whole degrees at or above 10°. */
export function formatHfovDeg(deg: number): string {
  if (deg < 1) return (Math.round(deg * 100) / 100).toString();
  return deg < 10 ? (Math.round(deg * 10) / 10).toString() : Math.round(deg).toString();
}

/** Parse the wide-end focal length from strings like "2.8mm", "2.8-12mm", "f=4.0mm". */
export function parseFocalMm(focalLengthStr: string | undefined): number | null {
  if (!focalLengthStr) return null;
  const range = focalLengthStr.match(/([\d.]+)\s*-\s*([\d.]+)/);
  const fStr = range ? range[1] : focalLengthStr.match(/([\d.]+)/)?.[1];
  if (!fStr) return null;
  const f = parseFloat(fStr);
  return Number.isFinite(f) && f > 0 ? f : null;
}

/** Parse a focal length spec into {min, max}. Returns null if not a valid varifocal range.
 *  Examples: "2.7-13.5mm" → {min: 2.7, max: 13.5}, "8mm - 32mm" → {min: 8, max: 32}.
 *  Fixed-focus strings ("2.8mm") return null — there's no range to slide. */
export function parseFocalRange(
  focalLengthStr: string | undefined,
): { min: number; max: number } | null {
  if (!focalLengthStr) return null;
  const m = focalLengthStr.match(/([\d.]+)\s*(?:mm)?\s*-\s*([\d.]+)\s*mm/i);
  if (!m) return null;
  const min = parseFloat(m[1]);
  const max = parseFloat(m[2]);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min <= 0 || max <= min) return null;
  return { min, max };
}

/** Compute HFOV in degrees from focal length string + sensor size string.
 *  Returns null if either input is missing or unrecognized. */
export function computeHfovFromOptics(
  focalLengthStr: string | undefined,
  sensorSize: string | undefined,
): number | null {
  if (!focalLengthStr || !sensorSize) return null;
  const w = SENSOR_SIZES[sensorSize]; // single source of truth (doriUtils)
  if (!w) return null;
  const f = parseFocalMm(focalLengthStr);
  if (f === null) return null;
  const hfovRad = 2 * Math.atan(w / (2 * f));
  return Math.round((hfovRad * 180 / Math.PI) * 10) / 10;
}

/**
 * Manufacturer-spec anchor for the focal↔angle conversion of ONE optic:
 * the datasheet's (focal, hfov) pair at the wide end, plus optionally the
 * second pair at the tele end when the datasheet publishes both ("103°–33°").
 */
export interface OpticsAnchor {
  focalMm: number;
  hfovDeg: number;
  teleFocalMm?: number;
  teleHfovDeg?: number;
  /** The law the lens's angle follows its focal by (data/publishedZoom.ts); absent is rectilinear. */
  law?: ZoomLaw;
}

/** Rectilinear is the default: w = 2 f tan(hfov / 2). Equisolid: w = 4 f sin(hfov / 4). */
export type ZoomLaw = 'equisolid';

/** Image width, mm, an angle fills at a focal under the law. */
function widthUnder(law: ZoomLaw | undefined, focalMm: number, hfovDeg: number): number {
  return law === 'equisolid'
    ? 4 * focalMm * Math.sin((hfovDeg * Math.PI) / 720)
    : 2 * focalMm * Math.tan((hfovDeg * Math.PI) / 360);
}

/** Angle, radians, an image width fills at a focal under the law. */
function angleUnder(law: ZoomLaw | undefined, widthMm: number, focalMm: number): number {
  return law === 'equisolid'
    ? 4 * Math.asin(Math.min(1, widthMm / (4 * focalMm)))
    : 2 * Math.atan(widthMm / (2 * focalMm));
}

/**
 * HFOV at an arbitrary focal length, ANCHORED to the manufacturer's spec
 * pair(s) instead of an assumed sensor class.
 *
 * The app is a simulator: picking a model sets the DATASHEET hfov, so the
 * zoom slider must reproduce exactly that value when it returns to the
 * anchor focal — converting through an assumed sensor made the cone JUMP
 * (e.g. 103° spec → 97° derived) on the first touch of the slider with no
 * real-world change (2026-08-11).
 *
 * One anchor pair implies the optic's effective sensor width from the spec
 * itself under the anchor's law, w = 2·f_a·tan(hfov_a/2) rectilinear or
 * 4·f_a·sin(hfov_a/4) equisolid, and the same law gives hfov(f). Exact at the
 * anchor by construction.
 *
 * TWO pairs (wide + tele) make BOTH ends exact. Real zooms are not perfectly
 * rectilinear-constant: the image circle can crop the sensor at the wide end,
 * so the effective width differs per end. Between the anchors the effective
 * width is interpolated linearly in focal length — a smooth physical model
 * that passes through every number the manufacturer published. Outside the
 * anchored span the nearer end's width is held (no extrapolation invention).
 */
export function hfovFromFocalAnchored(focalMm: number, anchor: OpticsAnchor): number | null {
  if (!(focalMm > 0) || !(anchor.focalMm > 0)) return null;
  if (!(anchor.hfovDeg > 0) || !(anchor.hfovDeg < 180)) return null;
  const law = anchor.law;
  const wWide = widthUnder(law, anchor.focalMm, anchor.hfovDeg);

  let w = wWide;
  const hasTele =
    typeof anchor.teleFocalMm === 'number' && typeof anchor.teleHfovDeg === 'number' &&
    anchor.teleFocalMm > anchor.focalMm &&
    anchor.teleHfovDeg > 0 && anchor.teleHfovDeg < anchor.hfovDeg;
  if (hasTele) {
    const fT = anchor.teleFocalMm as number;
    const wTele = widthUnder(law, fT, anchor.teleHfovDeg as number);
    const t = Math.min(1, Math.max(0, (focalMm - anchor.focalMm) / (fT - anchor.focalMm)));
    w = wWide + (wTele - wWide) * t;
  }

  const hfovRad = angleUnder(law, w, focalMm);
  return Math.round((hfovRad * 180 / Math.PI) * 10) / 10;
}

/** Build the camera-field patch for switching to a chosen lens variant.
 *  Reuses computeHfovFromOptics so the cone matches the varifocal-slider math.
 *  Resets currentFocalMm so a stale varifocal position from the previous lens
 *  doesn't override the new optic. Honours an explicit hfov on the variant. */
export function lensVariantPatch(
  variant: { focalLength: string; hfov?: number; range?: number; irRange?: number },
  sensorSize: string | undefined,
): { focalLength: string; currentFocalMm: undefined; opticsAnchor: OpticsAnchor | undefined; hfov?: number; range?: number; irRange?: number } {
  const hfov = typeof variant.hfov === 'number'
    ? variant.hfov
    : (computeHfovFromOptics(variant.focalLength, sensorSize) ?? undefined);
  return {
    focalLength: variant.focalLength,
    currentFocalMm: undefined,
    // Datasheet pair when the variant carries an explicit hfov: anchors the
    // zoom curve to the manufacturer's optic (simulator is 1:1 with the
    // spec); derived-hfov variants get no anchor.
    opticsAnchor: typeof variant.hfov === 'number'
      ? specOpticsAnchor(variant.focalLength, variant.hfov)
      : undefined,
    ...(typeof hfov === 'number' ? { hfov } : {}),
    ...(typeof variant.range === 'number' ? { range: variant.range } : {}),
    ...(typeof variant.irRange === 'number' ? { irRange: variant.irRange } : {}),
  };
}

/**
 * Build the spec anchor for an optic from its catalog fields: the wide end
 * of the focal range (or the fixed focal) paired with the datasheet hfov.
 * Null when either half of the pair is missing or implausible.
 */
export function specOpticsAnchor(
  focalLengthStr: string | undefined,
  specHfovDeg: number | undefined,
  specHfovTeleDeg?: number,
  law?: ZoomLaw,
): OpticsAnchor | undefined {
  if (typeof specHfovDeg !== 'number' || !(specHfovDeg > 0) || !(specHfovDeg < 180)) return undefined;
  const range = parseFocalRange(focalLengthStr);
  const focalMm = range ? range.min : parseFocalMm(focalLengthStr);
  if (focalMm === null || !(focalMm > 0)) return undefined;
  const withLaw = law ? { law } : {};
  // Second spec pair (datasheets publishing "103°–33°"): both zoom ends
  // become exact. Only a plausible pair is accepted — the tele angle must be
  // NARROWER than wide and the focal range must actually be a range.
  if (
    typeof specHfovTeleDeg === 'number' && range && range.max > range.min &&
    specHfovTeleDeg > 0 && specHfovTeleDeg < specHfovDeg
  ) {
    return { focalMm, hfovDeg: specHfovDeg, teleFocalMm: range.max, teleHfovDeg: specHfovTeleDeg, ...withLaw };
  }
  return { focalMm, hfovDeg: specHfovDeg, ...withLaw };
}

/** Sensor aspect ratio (height / width) inferred from resolution string.
 *  Falls back to 9/16 for 16:9 cameras when resolution is missing/unparseable. */
export function getSensorAspectHW(resolution: string | undefined): number {
  const pair = parseResolutionPair(resolution);
  return pair && pair.width > 0 ? pair.height / pair.width : 9 / 16;
}

/** Half-VFOV (rad) used for ground-intersection geometry — blind spot,
 *  near/far ground reach, elevation views. This is the canonical formula
 *  for every elevation calculation in the app; DO NOT inline a different
 *  formula elsewhere, math drift between rendering and exports breaks
 *  engineer trust.
 *
 *  Projection: EQUISOLID ANGLE, r = 2f·sin(θ/2), at every field angle.
 *
 *  It was rectilinear (r = f·tan θ) below 170° and equidistant (r = f·θ) above.
 *  Both parts were wrong, and the repo holds the evidence: 69 models carrying
 *  BOTH a manufacturer HFOV and VFOV, marked `verified`. Median error of each
 *  candidate against them, measured in lensProjection.test.ts:
 *
 *      rectilinear     r = f·tan θ        18.55°
 *      equidistant     r = f·θ             3.29°
 *      EQUISOLID       r = 2f·sin(θ/2)     1.89°
 *      orthographic    r = f·sin θ         2.66°
 *
 *  Equisolid wins in every HFOV band including the narrow one, so there is no
 *  threshold and no lens-class switch here: one law over 0-360°. That is what
 *  removes the old 170° cutoff, which moved VFOV from 162.1° to 95.6° across a
 *  tenth of a degree and made the dead zone fall, reach zero, then jump back up.
 *
 *  What the old form cost, always in the same direction: it OVERSTATED VFOV, so
 *  it drew every dead zone too small. A 130° lens 3 m up at 20° tilt showed a
 *  1.06 m dead zone against a real 2.07 m. For a tool used to prove there is no
 *  blind spot at a doorway, that is the error that matters.
 *
 *  Corridor mode is untouched and projection-independent: the sensor is rotated
 *  90°, so the lens's nominal HFOV simply becomes the vertical axis. */
export function getVfovHalfRad(
  rawHfovDeg: number,
  resolution: string | undefined,
  corridorMode?: boolean,
): number {
  if (corridorMode) return (rawHfovDeg * Math.PI) / 360;
  return equisolidHalfAngleRad((rawHfovDeg * Math.PI) / 180, getSensorAspectHW(resolution));
}

/**
 * Half-angle of the sensor's SHORT axis under the equisolid-angle projection.
 *
 * r = 2f·sin(θ/2) maps field angle to image radius, so the two half-extents sit
 * in the sensor's own ratio: sin(V/4) = sin(H/4) · (h/w). Clamped because eight
 * catalogue bodies store a 360° PAN RANGE in the hfov field, which would
 * otherwise ask for the arcsine of a number greater than one.
 */
function equisolidHalfAngleRad(hfovRad: number, aspect: number): number {
  if (!Number.isFinite(hfovRad) || hfovRad <= 0) return 0;
  return 2 * Math.asin(Math.min(1, Math.sin(Math.min(hfovRad, 2 * Math.PI) / 4) * aspect));
}

/** Convenience: full VFOV in degrees (used for ground-intersection geometry). */
export function getVfovDeg(
  rawHfovDeg: number,
  resolution: string | undefined,
  corridorMode?: boolean,
): number {
  return (getVfovHalfRad(rawHfovDeg, resolution, corridorMode) * 360) / Math.PI;
}

/**
 * Floor for mounting height, in metres.
 *
 * Zero is a singularity, not a low value. Every ground intersection solves
 * `t = h / -dir.up`, so at h = 0 each downward ray lands on the camera itself
 * and the footprint collapses to a point — from which nothing downstream can
 * recover: no cone, no shadows, no DORI. A camera "on the ground" is really a
 * camera a centimetre above it, and 1 cm is small enough to be invisible in any
 * readout (heights display to 2 dp) while keeping the geometry non-degenerate.
 */
export const MIN_MOUNT_HEIGHT_M = 0.01;

/**
 * Near blind / dead-zone GROUND distance (metres) under a tilted, mounted
 * camera: the closest distance at which the bottom of the FOV first reaches the
 * ground. = mountingHeight / tan(tilt + vfov/2).
 *
 * This is a PHYSICAL property of the install — it depends ONLY on mounting
 * height, the actual tilt angle, the lens VFOV, and, once the housing is turned
 * about its optical axis, the ROLL. Roll belongs in that list for a concrete
 * reason: it moves the frame's most downward ray off the centre-bottom and out
 * toward a corner, which reaches further down, so the ground starts CLOSER to
 * the mast than the level-sensor formula says. The `= mountingHeight / tan(tilt
 * + vfov/2)` below is the roll-0 case of that, exact and unchanged. It is
 * INDEPENDENT of the
 * display `range` (how far the FOV cone is drawn on the map) — there is
 * deliberately no `range` parameter, so the dead zone can never be coupled to
 * it. A telephoto/zoomed camera's dead zone grows because its VFOV narrows, not
 * because of any range value. Single source of truth for both the 2D renderer
 * (CameraRenderer blindspotPx) and the panel read-out, so they cannot diverge.
 *
 * Returns Infinity when the lower FOV edge looks above the horizon (no near
 * ground hit), and 0 when the camera sees ~straight down (no dead zone).
 */
export function nearBlindZoneM(
  mountingHeightM: number,
  tiltDeg: number,
  rawHfovDeg: number,
  resolution: string | undefined,
  corridorMode?: boolean,
  rollDeg?: number,
): number {
  const nearEdgeRad = frameEdgeRad(tiltDeg, rawHfovDeg, resolution, corridorMode, rollDeg, 'near');
  if (nearEdgeRad <= 0) return Infinity;            // looks above horizon
  if (nearEdgeRad >= Math.PI / 2 - 0.01) return 0;  // ~straight down → no dead zone
  const m = mountingHeightM / Math.tan(nearEdgeRad);
  return m > 0 ? m : 0;
}

/**
 * Elevation of the frame's most downward ('near') or most upward ('far') ray, in
 * radians below horizontal. The single place the two ground-reach formulas learn
 * where a frame's edges point.
 *
 * THE TWO EDGES ARE NOT TREATED ALIKE, and the asymmetry is the finding rather
 * than an oversight.
 *
 * NEAR keeps `tilt + vfov/2` whenever the sensor is level, because that
 * expression is not an approximation there, it is EXACT: nadir has no component
 * along a horizontal `right`, so its perpendicular foot on the bottom edge's
 * plane lands precisely on the centre-bottom ray, and the solver returns the
 * same number to nine decimals (pinned in cameraRoll.test.ts). Keeping it means
 * every project saved to date is answered by the very expression it always was.
 *
 * FAR goes through the solver ALWAYS, including at roll 0, because `tilt −
 * vfov/2` is wrong there and always has been. Once the top edge points BELOW the
 * horizon the corners of the frame sit shallower than its centre-top and
 * therefore land farther out — the opposite of the near edge, where length works
 * against the corners. Measured at roll 0, 4 m up: a 90° lens at 35° of tilt
 * reaches 30.4 m and not the 22.4 m the closed form reports; a 110° lens at 35°
 * reaches 74.5 m and not 46.7 m.
 *
 * This is a real change to a user-visible number on existing projects, made
 * deliberately for two reasons. It settles a contradiction rather than
 * introducing one: `computeGroundFootprint` samples the whole image border, so
 * the cone DRAWN on the map already extends to those corners, and the docstring
 * above promises the panel read-out and the drawn cone can never diverge. And
 * the alternative is worse — a closed form at roll 0 with the solver either side
 * of it would make the read-out JUMP the instant anyone nudged the housing,
 * 22.4 m to 30.4 m with no physics behind the step.
 *
 * The frame handed to the solver is the one the rest of the app already draws:
 * `getDisplayedHfovDeg` has corridor mode folded in, so a corridor camera enters
 * as a frame ALREADY turned 90°, and roll composes on top of it instead of
 * fighting it.
 */
export function frameEdgeRad(
  tiltDeg: number,
  rawHfovDeg: number,
  resolution: string | undefined,
  corridorMode: boolean | undefined,
  rollDeg: number | undefined,
  edge: 'near' | 'far',
): number {
  const vHalf = getVfovHalfRad(rawHfovDeg, resolution, corridorMode);
  const roll = Number.isFinite(rollDeg) ? (rollDeg as number) : 0;
  if (edge === 'near' && !roll) return (tiltDeg * Math.PI) / 180 + vHalf;
  const range = frameDepressionRangeDeg(
    tiltDeg,
    (getDisplayedHfovDeg(rawHfovDeg, resolution, corridorMode) * Math.PI) / 360,
    vHalf,
    roll,
  );
  const deg = edge === 'near' ? range.steepestDeg : range.shallowestDeg;
  return (deg * Math.PI) / 180;
}

/**
 * FAR ground reach (metres): the farthest distance the FOV footprint touches
 * the ground for a tilted, mounted camera — the symmetric partner of
 * nearBlindZoneM. The UPPER FOV edge meets the ground at
 * mountingHeight / tan(tilt − vfov/2); the result is then clamped to how far
 * the display `range` actually reaches ALONG THE GROUND, which is
 * √(range² − h²) and not `range` itself.
 *
 * That clamp used to be `range`, and it made this function contradict the
 * promise in the paragraph below. `range` is a straight-line distance from the
 * lens, so the set of points within it is a sphere about the lens and its
 * intersection with the ground is a circle of radius √(range² − h²). The
 * renderer has drawn it that way since `groundReachM` landed; the panel
 * read-out had not caught up, so on a 12 m mount with a 15 m range the panel
 * said 15 m over a cone that stops at 9 m.
 *
 * When the upper edge points at or above the horizon (tilt ≤ vfov/2) the ray
 * never hits the ground, so the footprint reaches the full display `range`
 * (returns rangeM). This MATCHES the frustum renderer's above-horizon clamp
 * (rayGroundHit) — same getVfovHalfRad, same clamp — so the panel "effective
 * range" read-out and the drawn cone can never diverge. Zooming a tilted
 * camera narrows vfov, pushing the upper edge below the horizon, which SHRINKS
 * this far reach toward the aim point h/tan(tilt): correct optics, not a range
 * bug. The lever to reach farther is LOWERING the tilt, not raising `range`.
 *
 * Single source of truth for the panel's effective-range hint. INDEPENDENT of
 * pan/azimuth, which is structural rather than a convention: turning about the
 * vertical moves no ray up or down. Roll does, and is honoured — the upper edge
 * of a rolled frame clears the horizon sooner, so this reaches FARTHER, the
 * mirror of what roll does to the dead zone. At roll 0 it is the centre-line
 * upper ray, exactly as before.
 */
export function farGroundReachM(
  mountingHeightM: number,
  tiltDeg: number,
  rawHfovDeg: number,
  resolution: string | undefined,
  rangeM: number,
  corridorMode?: boolean,
  rollDeg?: number,
): number {
  const reachM = groundReachM(rangeM, mountingHeightM);
  // No ground inside the range circle at all: the LOWER edge of the frustum
  // first meets the ground beyond it, so the footprint is empty and the
  // renderer draws nothing. Returning a positive number here is what let the
  // panel advertise "14.70 m effective" beside "19.90 m dead zone" for a camera
  // covering no ground whatsoever — on a 16:9 sensor that is any level camera
  // with a lens narrower than about 60°, which is most of the catalog.
  const nearM = nearBlindZoneM(mountingHeightM, tiltDeg, rawHfovDeg, resolution, corridorMode, rollDeg);
  if (!(nearM < reachM)) return 0;
  const farEdgeRad = frameEdgeRad(tiltDeg, rawHfovDeg, resolution, corridorMode, rollDeg, 'far');
  if (farEdgeRad <= 0) return reachM;            // upper edge at/above horizon → full ground reach
  const m = mountingHeightM / Math.tan(farEdgeRad);
  return m > 0 ? Math.min(reachM, m) : reachM;
}

/** HFOV displayed on the map (full angle, degrees).
 *  Standard: equals the lens's nominal HFOV.
 *  Corridor: the cone width on the map is the lens's nominal VFOV (sensor
 *  rotated 90°). Uses the proper tan-based aspect conversion. */
export function getDisplayedHfovDeg(
  rawHfovDeg: number,
  resolution: string | undefined,
  corridorMode?: boolean,
): number {
  if (!corridorMode) return rawHfovDeg;
  // Corridor: the cone drawn on the map is the lens's nominal VFOV, so it goes
  // through the SAME conversion getVfovHalfRad uses. Deriving it any other way
  // would let the map width and the ground intersection disagree about one
  // rotated sensor.
  return (equisolidHalfAngleRad((rawHfovDeg * Math.PI) / 180, getSensorAspectHW(resolution)) * 360) / Math.PI;
}
